import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Polygon, useMap } from "react-leaflet";
import { Layers } from "lucide-react";
import "leaflet/dist/leaflet.css";

const BAND_COLOR = {
  green: "#2F8B5E",
  amber: "#B8842A",
  red: "#B7402A",
  default: "#6B6B7B",
};

// Rough polygonal boundaries for Chennai's five operational regions.
// These are demo approximations of the metro-area zones, not survey-grade
// geometries — good enough to give a visual "region context" wash under the
// branch pins.
const REGION_POLYGONS = {
  North:   [[13.095, 80.145], [13.230, 80.170], [13.230, 80.320], [13.095, 80.320]],
  Central: [[13.020, 80.195], [13.095, 80.195], [13.095, 80.270], [13.020, 80.270]],
  East:    [[13.020, 80.270], [13.095, 80.270], [13.095, 80.360], [13.020, 80.360]],
  South:   [[12.845, 80.160], [13.020, 80.160], [13.020, 80.290], [12.845, 80.290]],
  West:    [[13.020, 80.060], [13.155, 80.060], [13.155, 80.195], [13.020, 80.195]],
};

const bandForScore = (s) => (s >= 78 ? "green" : s >= 62 ? "amber" : "red");

// Recenter the map when focus changes. Uses animate:false so an in-flight
// leaflet transition can't fire after the map has been unmounted (which
// throws a TypeError on _leaflet_pos when the user drills into a branch).
const Recenter = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom, { animate: false });
  }, [center, zoom, map]);
  return null;
};

export const BranchMap = ({ allBranches = [], leaderboard = [], focusBranchId, onSelect }) => {
  const [showChoropleth, setShowChoropleth] = useState(true);

  // Map leaderboard rows (which carry health data) by branch_id
  const health = useMemo(() => {
    const m = {};
    (leaderboard || []).forEach((r) => {
      m[r.branch_id] = {
        health_score: r.health_score, health_band: r.health_band,
        todays_revenue: r.todays_revenue, wow: r.wow_delta_pct,
        wastage_flag: r.wastage_flag, stockout_flag: r.stockout_flag, staffing_flag: r.staffing_flag,
        rank: r.rank,
      };
    });
    return m;
  }, [leaderboard]);

  // Aggregate branch health per region → drives the choropleth wash.
  const regionAgg = useMemo(() => {
    const buckets = {};
    allBranches.forEach((b) => {
      const h = health[b.id];
      if (!h || !b.region) return;
      buckets[b.region] = buckets[b.region] || { sum: 0, count: 0, revenue: 0 };
      buckets[b.region].sum += h.health_score;
      buckets[b.region].count += 1;
      buckets[b.region].revenue += h.todays_revenue || 0;
    });
    const out = {};
    Object.keys(buckets).forEach((k) => {
      const b = buckets[k];
      const avg = b.count ? b.sum / b.count : 0;
      out[k] = {
        avg_health: Math.round(avg),
        band: bandForScore(avg),
        count: b.count,
        revenue: b.revenue,
      };
    });
    return out;
  }, [allBranches, health]);

  const focusBranch = allBranches.find((b) => b.id === focusBranchId);
  const center = focusBranch
    ? [focusBranch.latitude, focusBranch.longitude]
    : [13.04, 80.20]; // Chennai centre
  const zoom = focusBranch ? 13 : 11;

  return (
    <div className="ang-panel overflow-hidden" data-testid="branch-map">
      <header className="border-b border-ang px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ang-muted">Location Map</div>
          <h2 className="font-serif text-2xl mt-1">Chennai branches · health at a glance</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-ang-muted">
            <LegendPin color={BAND_COLOR.green} label="Green ≥78" />
            <LegendPin color={BAND_COLOR.amber} label="Amber ≥62" />
            <LegendPin color={BAND_COLOR.red} label="Red <62" />
          </div>
          <button
            type="button"
            onClick={() => setShowChoropleth((v) => !v)}
            data-testid="btn-toggle-choropleth"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold border transition-colors ${
              showChoropleth ? "text-[#14142B]" : "text-ang-muted"
            }`}
            style={{
              background: showChoropleth ? "#FBF0DC" : "#FFFFFF",
              borderColor: showChoropleth ? "rgba(184,132,42,0.4)" : "#E7E4DC",
            }}
            aria-pressed={showChoropleth}
            title="Toggle region heat overlay"
          >
            <Layers size={11} strokeWidth={2} />
            Region heat
          </button>
        </div>
      </header>
      <div style={{ height: 420 }} className="relative">
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />
          <Recenter center={center} zoom={zoom} />

          {/* Choropleth wash — one polygon per region, tinted by avg health */}
          {showChoropleth && Object.entries(REGION_POLYGONS).map(([regionName, poly]) => {
            const agg = regionAgg[regionName];
            const band = agg?.band || "default";
            const color = BAND_COLOR[band] || BAND_COLOR.default;
            return (
              <Polygon
                key={regionName}
                positions={poly}
                pathOptions={{
                  color,
                  weight: 1.2,
                  opacity: 0.55,
                  fillColor: color,
                  fillOpacity: agg ? 0.22 : 0.08,
                  dashArray: "4 4",
                }}
              >
                <Tooltip direction="center" sticky opacity={0.95}>
                  <div style={{ fontFamily: "Inter", fontSize: 12, minWidth: 150 }}>
                    <div style={{ fontFamily: "Source Serif 4, Georgia, serif", fontSize: 14, marginBottom: 2 }}>
                      {regionName} Chennai
                    </div>
                    {agg ? (
                      <>
                        <div style={{ color: "#6B6B7B", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                          {agg.count} branch{agg.count === 1 ? "" : "es"} · region avg
                        </div>
                        <div style={{ marginTop: 4 }}>
                          Avg health <b style={{ color, fontVariantNumeric: "tabular-nums" }}>{agg.avg_health}</b>
                          <span style={{ color: "#6B6B7B" }}> · {band}</span>
                        </div>
                        <div style={{ color: "#6B6B7B", fontSize: 11 }}>
                          Today ₹{(agg.revenue / 1e5).toFixed(2)}L
                        </div>
                      </>
                    ) : (
                      <div style={{ color: "#6B6B7B", fontSize: 11 }}>No branches in scope</div>
                    )}
                  </div>
                </Tooltip>
              </Polygon>
            );
          })}

          {allBranches.map((b) => {
            const h = health[b.id];
            const band = h?.health_band || "default";
            const color = BAND_COLOR[band] || BAND_COLOR.default;
            const isFocus = b.id === focusBranchId;
            return (
              <CircleMarker
                key={b.id}
                center={[b.latitude, b.longitude]}
                radius={isFocus ? 14 : 9}
                pathOptions={{
                  color: isFocus ? "#14142B" : color,
                  weight: isFocus ? 3 : 2,
                  fillColor: color,
                  fillOpacity: 0.82,
                }}
                eventHandlers={{
                  click: () => onSelect && onSelect(b.id),
                }}
              >
                <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                  <div style={{ fontFamily: "Inter", fontSize: 12, minWidth: 160 }}>
                    <div style={{ fontFamily: "Source Serif 4, Georgia, serif", fontSize: 14, marginBottom: 2 }}>
                      {b.name}
                    </div>
                    <div style={{ color: "#6B6B7B", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      {b.region} Chennai {h?.rank ? `· Rank ${h.rank}` : ""}
                    </div>
                    {h && (
                      <>
                        <div style={{ marginTop: 6 }}>
                          Health <b style={{ color, fontVariantNumeric: "tabular-nums" }}>{h.health_score}</b>
                          <span style={{ color: "#6B6B7B" }}> · {band}</span>
                        </div>
                        <div style={{ color: "#6B6B7B", fontSize: 11 }}>
                          Today ₹{(h.todays_revenue / 1e5).toFixed(2)}L
                          &nbsp;·&nbsp;
                          <span style={{ color: h.wow >= 0 ? "#2F8B5E" : "#B7402A" }}>
                            {h.wow >= 0 ? "+" : ""}{h.wow}%
                          </span>
                        </div>
                        {(h.wastage_flag || h.stockout_flag || h.staffing_flag) && (
                          <div style={{ fontSize: 10, color: "#B7402A", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                            {[h.wastage_flag && "wastage", h.stockout_flag && "stock-out", h.staffing_flag && "staffing"].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </>
                    )}
                    <div style={{ marginTop: 6, fontSize: 10, color: "#B8842A", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      click pin to focus
                    </div>
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

const LegendPin = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: color }} />
    {label}
  </span>
);

export default BranchMap;
